import { entries, isArray } from '@rehaje/shared'

import type {
  ArrowFunctionExpression,
  AssignmentExpression,
  AssignmentOperator,
  BinaryExpression,
  BinaryOperator,
  BlockStatement,
  CallExpression,
  ChainExpression,
  ClassDeclaration,
  ConditionalExpression,
  ExpressionStatement,
  FunctionDeclaration,
  FunctionExpression,
  Identifier,
  IfStatement,
  ImportDefaultSpecifier,
  ImportNamespaceSpecifier,
  ImportSpecifier,
  JSXEmptyExpression,
  JSXIdentifier,
  JSXSpreadAttribute,
  Literal,
  LogicalExpression,
  LogicalOperator,
  MemberExpression,
  NewExpression,
  Node,
  ObjectExpression,
  ObjectPattern,
  Program,
  Property,
  ReturnStatement,
  SpreadElement,
  ThrowStatement,
  UpdateExpression,
  VariableDeclaration,
  VariableDeclarator,
  WithStatement
} from 'acorn'

export const t = {
  arrowFunctionExpression(params: Node[], body: Node) {
    return {
      body,
      params,
      type: 'ArrowFunctionExpression'
    } as ArrowFunctionExpression
  },
  assignmentExpression(operator: AssignmentOperator, left: Node, right: Node) {
    return {
      left,
      operator,
      right,
      type: 'AssignmentExpression'
    } as AssignmentExpression
  },
  binaryExpression(operator: BinaryOperator, left: Node, right: Node) {
    return {
      left,
      operator,
      right,
      type: 'BinaryExpression'
    } as BinaryExpression
  },
  blockStatement(body: Node[]) {
    return {
      body,
      type: 'BlockStatement'
    } as BlockStatement
  },
  callExpression(callee: Node, args: Node[], optional = false) {
    return {
      arguments: args,
      callee,
      optional,
      type: 'CallExpression'
    } as CallExpression
  },
  chainExpression(expression: Node) {
    return {
      expression,
      type: 'ChainExpression'
    } as ChainExpression
  },
  conditionalExpression(test: Node, consequent: Node, alternate?: Node) {
    return {
      alternate,
      consequent,
      test,
      type: 'ConditionalExpression'
    } as ConditionalExpression
  },
  expressionStatement(expression: Node) {
    return {
      expression,
      type: 'ExpressionStatement'
    } as ExpressionStatement
  },
  functionDeclaration(id: Node, params: Node[], body: Node) {
    return {
      body,
      id,
      params,
      type: 'FunctionDeclaration'
    } as FunctionDeclaration
  },
  functionExpression(params: Node[], body: Node, async = false) {
    return {
      async,
      body,
      params,
      type: 'FunctionExpression'
    } as FunctionExpression
  },
  identifier(name?: any) {
    return {
      name: String(name),
      type: 'Identifier'
    } as Identifier
  },
  ifStatement(test: Node, consequent: Node, alternate?: Node) {
    return {
      alternate,
      consequent,
      test,
      type: 'IfStatement'
    } as IfStatement
  },
  isBlockStatement(node?: Node): node is BlockStatement {
    return node?.type === 'BlockStatement'
  },
  isClassDeclaration(node?: Node): node is ClassDeclaration {
    return node?.type === 'ClassDeclaration'
  },
  isEmptyStatement(node?: Node): node is ExpressionStatement {
    return node?.type === 'ExpressionStatement'
  },
  isFunctionDeclaration(node?: Node): node is FunctionDeclaration {
    return node?.type === 'FunctionDeclaration'
  },
  isFunctionExpression(node?: Node): node is FunctionExpression {
    return node?.type === 'FunctionExpression'
  },
  isIdentifier(node?: Node): node is Identifier {
    return node?.type === 'Identifier'
  },
  isImportDefaultSpecifier(node?: Node): node is ImportDefaultSpecifier {
    return node?.type === 'ImportDefaultSpecifier'
  },
  isImportNamespaceSpecifier(node?: Node): node is ImportNamespaceSpecifier {
    return node?.type === 'ImportNamespaceSpecifier'
  },
  isImportSpecifier(node?: Node): node is ImportSpecifier {
    return node?.type === 'ImportSpecifier'
  },
  isJSXEmptyExpression(node?: Node): node is JSXEmptyExpression {
    return node?.type === 'JSXEmptyExpression'
  },
  isJSXIdentifier(node?: Node): node is JSXIdentifier {
    return node?.type === 'JSXIdentifier'
  },
  isJSXSpreadAttribute(node?: Node): node is JSXSpreadAttribute {
    return node?.type === 'JSXSpreadAttribute'
  },
  isLiteral(node?: Node): node is Literal {
    return node?.type === 'Literal'
  },
  isProgram(node?: Node): node is Program {
    return node?.type === 'Program'
  },
  isProperty(node?: Node): node is Property {
    return node?.type === 'Property'
  },
  isVariableDeclaration(node?: Node): node is VariableDeclaration {
    return node?.type === 'VariableDeclaration'
  },
  isVariableDeclarator(node?: Node): node is VariableDeclarator {
    return node?.type === 'VariableDeclarator'
  },
  literal(value: any) {
    return {
      raw: JSON.stringify(value),
      type: 'Literal',
      value
    } as Literal
  },
  logicalExpression(operator: LogicalOperator, left: Node, right: Node) {
    return {
      left,
      operator,
      right,
      type: 'LogicalExpression'
    } as LogicalExpression
  },
  memberExpression(object: Node, property: Node) {
    return {
      computed: false,
      object,
      optional: false,
      property,
      type: 'MemberExpression'
    } as MemberExpression
  },
  newExpression(callee: Node, args: Node[]) {
    return {
      arguments: args,
      callee,
      type: 'NewExpression'
    } as NewExpression
  },
  objectExpression(properties: Node[]) {
    return {
      properties,
      type: 'ObjectExpression'
    } as ObjectExpression
  },
  objectPattern(properties: Node[]) {
    return {
      properties,
      type: 'ObjectPattern'
    } as ObjectPattern
  },
  program(body: Node[]) {
    return {
      body,
      type: 'Program'
    } as Program
  },
  property(key: Node, value: Node) {
    return {
      computed: false,
      key,
      kind: 'init',
      method: false,
      shorthand:
        (t.isIdentifier(key) &&
          t.isIdentifier(value) &&
          key.name === value.name) ||
        (t.isLiteral(key) && t.isLiteral(value) && key.value === value.value),
      type: 'Property',
      value
    } as Property
  },
  returnStatement(argument?: Node) {
    return {
      argument,
      type: 'ReturnStatement'
    } as ReturnStatement
  },
  spreadElement(argument: Node) {
    return {
      argument,
      type: 'SpreadElement'
    } as SpreadElement
  },
  throwStatement(argument: Node) {
    return {
      argument,
      type: 'ThrowStatement'
    } as ThrowStatement
  },
  updateExpression(operator: string, argument: Node) {
    return {
      argument,
      operator,
      prefix: false,
      type: 'UpdateExpression'
    } as UpdateExpression
  },
  variableDeclaration(
    kind: VariableDeclaration['kind'],
    declarations: VariableDeclarator[]
  ) {
    return {
      declarations,
      kind,
      type: 'VariableDeclaration'
    } as VariableDeclaration
  },
  variableDeclarator(id: Node, init: Node) {
    return {
      id,
      init,
      type: 'VariableDeclarator'
    } as VariableDeclarator
  },
  withStatement(object: Node, body: Node) {
    return {
      body,
      object,
      type: 'WithStatement'
    } as WithStatement
  }
}

export function replaceNode(
  ancestors: Node[],
  replaceNode: (Node | null | undefined)[] | Node | null | undefined
) {
  const nodes = isArray(replaceNode)
    ? replaceNode.filter(Boolean)
    : [replaceNode].filter(Boolean)
  const firstNode = nodes[0]
  if (!firstNode) return
  const oldNode = ancestors.at(-1) as any
  const parentNode = ancestors.at(-2) as any
  for (const [key, value] of entries(parentNode)) {
    if (isArray(value) && value.includes(oldNode)) {
      value.splice(value.indexOf(oldNode), 1, ...nodes)
      break
    } else if (value === oldNode) {
      parentNode[key] = firstNode
      break
    }
  }
}
